/**
 * Collapsible panel listing the audit vendor files currently staged for the
 * active dealer group, with per-file delete.
 *
 * Mirrors components/photos/ActiveVendorFilesPanel.tsx (default collapsed,
 * zebra table, red-X delete in an "Actions" column). Status / freshness lives
 * in the File Requirements panel above; this panel is the deletable list.
 */
import React, { useState } from "react"
import { ChevronDown, ChevronRight, X } from "lucide-react"

import { AuditUploadedFile, deleteAuditFile } from "../../api/audit"
import { THEME } from "../../config/theme"
import { formatUploadedAt } from "../../utils/formatAge"

const VENDOR_LABELS: Record<string, string> = {
  tekion: "Tekion",
  rapidrecon: "Rapid Recon",
  homenet: "HomeNet",
}

interface Props {
  dealerGroupId: number
  files: AuditUploadedFile[]
  // Called after a file is successfully deleted so the parent can refetch.
  onFileDeleted: () => void
}

export default function AuditActiveFilesPanel({ dealerGroupId, files, onFileDeleted }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Delete "${filename}"?`)) return
    setDeletingFilename(filename)
    setDeleteError(null)
    try {
      await deleteAuditFile(filename, dealerGroupId)
      onFileDeleted()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed")
    } finally {
      setDeletingFilename(null)
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          cursor: "pointer",
          userSelect: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        <span style={{ width: 14, display: "inline-flex", alignItems: "center", color: THEME.colors.neutral.light.textSecondary }}>
          {expanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
        </span>
        Active Vendor Files
        <span style={{ fontWeight: 400, color: THEME.colors.neutral.light.textSecondary, marginLeft: 4 }}>
          ({files.length})
        </span>
      </div>

      {expanded && (
        <div style={{ padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}` }}>
          {deleteError && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "8px 12px",
                borderRadius: THEME.borderRadius.md,
                marginBottom: 8,
                fontSize: 12,
              }}
            >
              {deleteError}
            </div>
          )}
          {files.length === 0 ? (
            <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
              No files uploaded yet
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${THEME.colors.silver}`, textAlign: "left" }}>
                  <th style={thStyle}>Filename</th>
                  <th style={thStyle}>Vendor</th>
                  <th style={thStyle}>Size</th>
                  <th style={thStyle}>Uploaded</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => {
                  const isDeleting = deletingFilename === f.filename
                  return (
                    <tr
                      key={`${f.filename}-${i}`}
                      style={{
                        backgroundColor:
                          i % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain,
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      <td style={tdStyle} title={f.filename}>
                        {f.filename.length > 60 ? f.filename.slice(0, 60) + "..." : f.filename}
                      </td>
                      <td style={tdStyle}>{VENDOR_LABELS[f.vendor] ?? f.vendor}</td>
                      <td style={tdStyle}>{f.size_kb.toLocaleString()} KB</td>
                      {/* The backend has always sent `modified`; the table just
                          never showed it, so an operator could not tell a file
                          staged today from one left over from a previous day. */}
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        {formatUploadedAt(f.modified) || "—"}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => handleDelete(f.filename)}
                          disabled={isDeleting}
                          title="Delete"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: "none",
                            border: "none",
                            color: THEME.colors.semantic.danger,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            padding: "2px 6px",
                          }}
                        >
                          <X size={16} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontWeight: 500,
  fontSize: 12,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const tdStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 300,
}
